import React from "react";
import Modal from "react-modal";
import { Field, Form, Formik } from "formik";
import { Oval } from "react-loader-spinner";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  toggleShowEditDumpsterModal,
  updateDumpster,
  setSelectedDumpster,
} from "../../dumpstersSlice";
import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import { RadioButton } from "../../../Shared";
import { Dumpster, DumpsterSize } from "../../../../models";

import "./EditDumpsterModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

const validationSchema = Yup.object().shape({
  label: Yup.string().required("Label is required"),
  dumpsterSize: Yup.string().required("Dumpster size is required"),
});

export type EditDumpsterFormModel = Dumpster;

export function EditDumpsterModal() {
  const dispatch = useAppDispatch();

  const selectedDumpster = useAppSelector(
    (state) => state.dumpsters.selectedDumpster
  );
  const isOpen = useAppSelector(
    (state) => state.dumpsters.showEditDumpsterModal
  );
  const updateDumpsterAPI = useAppSelector(
    (state) => state.dumpsters.updateDumpster
  );

  React.useEffect(() => {
    if (!updateDumpsterAPI.loading && !!updateDumpsterAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !updateDumpsterAPI.loading &&
      !updateDumpsterAPI.error &&
      updateDumpsterAPI.value
    ) {
      toast.success("Successfully updated dumpster.");
      dispatch(toggleShowEditDumpsterModal());
      dispatch(setSelectedDumpster(undefined));
    }
  }, [
    updateDumpsterAPI.loading,
    !!updateDumpsterAPI.error,
    !!updateDumpsterAPI.value,
    dispatch,
  ]);

  const toggleModal = () => {
    dispatch(toggleShowEditDumpsterModal());
    dispatch(setSelectedDumpster(undefined));
  };

  const onSubmit = (form: EditDumpsterFormModel) => {
    dispatch(updateDumpster(form));
  };

  if (!selectedDumpster) return null;

  const initialValues: EditDumpsterFormModel = {
    ...selectedDumpster,
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={toggleModal} style={modalStyles}>
      <div className="update-dumpster-modal-component">
        <Close className="close-button" onClick={toggleModal} />

        <div className="title">Update Dumpster</div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form className="update-dumpster-form">
              <div className="field-container">
                <div className="label">Label:</div>
                <Field id="label" name="label" className="text-input" />
                {errors.label && touched.label && (
                  <div className="form-error">{errors.label}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Dumpster Size:</div>
                <div className="radio-button-options-container">
                  <RadioButton
                    name="dumpsterSize"
                    value={DumpsterSize.SIZE_10}
                    label="10 yards"
                  />
                  <RadioButton
                    name="dumpsterSize"
                    value={DumpsterSize.SIZE_15}
                    label="15 yards"
                  />
                  <RadioButton
                    name="dumpsterSize"
                    value={DumpsterSize.SIZE_CLEAN_FILL}
                    label="Clean Fill"
                  />
                </div>
                {errors.dumpsterSize && touched.dumpsterSize && (
                  <div className="form-error">{errors.dumpsterSize}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Fill Status:</div>
                <div className="radio-button-options-container">
                  <RadioButton name="fillStatus" value="empty" label="Empty" />
                  <RadioButton name="fillStatus" value="half" label="Half" />
                  <RadioButton name="fillStatus" value="full" label="Full" />
                </div>
              </div>

              <div className="field-container">
                <div className="label">Notes:</div>
                <Field
                  id="notes"
                  name="notes"
                  as="textarea"
                  className="text-input"
                  rows={3}
                />
              </div>

              <button
                type="submit"
                className="add-button"
                disabled={updateDumpsterAPI.loading}
              >
                Update Dumpster
                {updateDumpsterAPI.loading && (
                  <div style={{ marginLeft: 8 }}>
                    <Oval
                      color="#131313"
                      height={20}
                      width={20}
                      secondaryColor="#FBD20A"
                    />
                  </div>
                )}
              </button>

              {!!updateDumpsterAPI.error && (
                <div className="error-text">
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
